import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { aboutContent } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pelqli2vhsdh2d32og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutContent.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 韩漫漫画网 | 韩漫在线阅读平台"
        description="韩漫漫画网品牌介绍，国内领先的韩国漫画在线免费阅读平台，提供无删减下拉式韩漫大全。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="韩漫漫画网,韩漫在线阅读,关于我们,联系我们"
        canonicalUrl="https://hanmanmh.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              <strong>关于我们</strong>
            </h1>
            <p className="text-sm text-foreground-500 mt-1">了解韩漫漫画网</p>
          </div>
        </div>

        {/* Brand Intro */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-4">
              {aboutContent.brandName}
            </h2>
            <p className="text-primary-600 font-medium text-sm">{aboutContent.slogan}</p>
          </div>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed text-center max-w-3xl mx-auto">
            {aboutContent.introduction}
          </p>
        </section>

        {/* Features */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
              我们的<strong>优势</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutContent.features.map((feature, index) => (
                <div
                  key={index}
                  className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                    <i className={`${feature.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {aboutContent.stats.map((stat) => (
              <div
                key={stat.label}
                className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-600 font-heading">{stat.value}</div>
                <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
              用户<strong>口碑</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {aboutContent.testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-secondary-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
            常见<strong>问题</strong>
          </h2>
          <div className="space-y-3">
            {aboutContent.faq.map((item, index) => (
              <details
                key={index}
                className="group border border-background-200 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                  <span className="text-sm font-medium text-foreground-800 pr-4">{item.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-8">
              <strong>联系我们</strong>
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact_name"
                    name="name"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact_subject"
                    name="subject"
                    required
                    placeholder="请输入留言主题"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact_message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请输入您的留言内容（最多500字）"
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>提交中...
                    </span>
                  ) : (
                    '提交留言'
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 p-3 bg-accent-50 text-accent-700 rounded-lg text-sm">
                    <i className="ri-checkbox-circle-line"></i>
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-primary-50 text-primary-700 rounded-lg text-sm">
                    <i className="ri-error-warning-line"></i>
                    {formMessage}
                  </div>
                )}
              </form>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">邮箱</p>
                  <p className="text-sm text-foreground-700">{aboutContent.contactInfo.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                  <i className="ri-wechat-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">微信公众号</p>
                  <p className="text-sm text-foreground-700">{aboutContent.contactInfo.wechat}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-secondary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">服务范围</p>
                  <p className="text-sm text-foreground-700">{aboutContent.contactInfo.address}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}